/**
 * Conexión de agentes de IA (servidor MCP) y "Agentes conectados" — OT-0043.
 *
 * Contrato: backend `app/api/v1/agent_connections.py` (Docs/15 §3). Los DTOs
 * se mantienen en snake_case, tal como los envía la API.
 */

import { apiFetch } from './client'

/** Scope de autoría (OT-0006): crear y editar borradores de plantillas y
 * metodologías. Lo decide el usuario en la pantalla de conexión. */
export const AUTHOR_SCOPE = 'nexus.author'

export interface ConnectionRequest {
  client_id: string
  client_name: string
  client_domain: string
  redirect_host: string
  scopes: string[]
  /** False si la cuenta no incluye acceso de agentes (tier `external`). */
  allowed: boolean
  /** True si puede permitir también la autoría (admin): se ofrece la casilla. */
  can_author: boolean
}

export interface ClientRedirect {
  redirect_url: string
}

export interface AgentConnection {
  id: string
  client_id: string
  client_name: string
  client_domain: string
  scopes: string[]
  owner_user_id: string
  owner_name: string
  created_at: string
  last_used_at: string | null
}

export interface AgentServer {
  mcp_url: string
}

export type AgentConnectionScope = 'mine' | 'tenant'

export async function getConnectionRequest(
  accessToken: string,
  requestId: string,
): Promise<ConnectionRequest> {
  return apiFetch<ConnectionRequest>(
    `/api/v1/agent-connections/requests/${encodeURIComponent(requestId)}`,
    { accessToken },
  )
}

export async function approveConnectionRequest(
  accessToken: string,
  requestId: string,
  allowAuthoring: boolean,
): Promise<ClientRedirect> {
  return apiFetch<ClientRedirect>(
    `/api/v1/agent-connections/requests/${encodeURIComponent(requestId)}/approve`,
    { accessToken, method: 'POST', body: { allow_authoring: allowAuthoring } },
  )
}

export async function denyConnectionRequest(
  accessToken: string,
  requestId: string,
): Promise<ClientRedirect> {
  return apiFetch<ClientRedirect>(
    `/api/v1/agent-connections/requests/${encodeURIComponent(requestId)}/deny`,
    { accessToken, method: 'POST' },
  )
}

export async function listAgentConnections(
  accessToken: string,
  scope: AgentConnectionScope,
): Promise<AgentConnection[]> {
  return apiFetch<AgentConnection[]>(`/api/v1/agent-connections?scope=${scope}`, {
    accessToken,
  })
}

export async function revokeAgentConnection(
  accessToken: string,
  connectionId: string,
): Promise<void> {
  return apiFetch<void>(`/api/v1/agent-connections/${encodeURIComponent(connectionId)}`, {
    accessToken,
    method: 'DELETE',
  })
}

export async function getAgentServer(accessToken: string): Promise<AgentServer> {
  return apiFetch<AgentServer>('/api/v1/agent-connections/server', { accessToken })
}
